import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";

import { Box, IconButton, useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import { ToolbarProps, View } from "react-big-calendar";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";

const calendarViews = [
  { id: 1, view: "day" },
  { id: 2, view: "week" },
  { id: 3, view: "month" },
] as const;

interface CalendarToolbarProps extends ToolbarProps {
  isVendor?: boolean;
  isLocked?: boolean;
  onSetAvailability?: () => void;
  onNewBooking?: () => void;
  onBookAppointment?: () => void;
  onFilter?: () => void;
  filterRef?: React.Ref<HTMLDivElement>;
}

const Toolbar: React.FC<CalendarToolbarProps> = ({
  label,
  onNavigate,
  onView,
  isVendor = false,
  isLocked = false,
  onSetAvailability,
  onNewBooking,
  onBookAppointment,
  onFilter,
  filterRef,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const [calendarView, setCalendarView] = useState("month");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        // Everything on one row needs ~890px. Wrapping lets the date, the view
        // picker and the actions fall onto their own lines as space runs out.
        flexWrap: "wrap",
        paddingBottom: theme.spacing(1),
        gap: theme.spacing(1),
      }}
    >
      <IconButton onClick={() => onNavigate("PREV")}>
        <ArrowCircleLeftIcon color="warning" />
      </IconButton>

      <Box sx={{ flex: { xs: 1, sm: "0 0 auto" }, minWidth: 0 }}>
        <Typography variant="body2" align="center">
          {label}
        </Typography>

        <Box
          sx={{
            width: { xs: "100%", sm: "200px" },
            height: "5px",
            background: theme.palette.warning.main,
          }}
        />
      </Box>

      <IconButton onClick={() => onNavigate("NEXT")}>
        <ArrowCircleRightIcon color="warning" />
      </IconButton>

      <Box
        sx={{
          width: { xs: "100%", sm: "160px" },
          marginLeft: { sm: theme.spacing(2) },
        }}
      >
        <AutoComplete
          options={calendarViews.map((item) => ({
            id: item.id,
            label: t(`views.${item.view}`),
          }))}
          value={
            !calendarView
              ? null
              : (calendarViews.find((option) => option.view === calendarView)
                  ?.id ?? 1)
          }
          onChange={(value) => {
            if (!value) {
              return;
            }

            const picked = calendarViews.find(({ id }) => id === value.id);
            if (picked) {
              setCalendarView(picked.view);
              onView(picked.view as View);
            }
          }}
        />
      </Box>

      {/* Right-aligned actions */}
      <Box
        sx={{
          ml: { sm: "auto" },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          // Share the row evenly on a phone rather than overflowing it.
          "& > *": { flex: { xs: "1 1 0", sm: "0 0 auto" } },
        }}
      >
        <Box ref={filterRef} sx={{ display: "inline-flex" }}>
          <Button
            variant="outlined"
            color="primary"
            disabled={isLocked}
            onClick={onFilter}
            startAdornment={<FilterListIcon fontSize="small" />}
          >
            {t("toolbar.filter")}
          </Button>
        </Box>

        {isVendor && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onSetAvailability}
            startAdornment={<EventAvailableIcon fontSize="small" />}
          >
            {t("toolbar.set_availability")}
          </Button>
        )}

        {isVendor && (
          <Button
            variant="contained"
            color="warning"
            disabled={isLocked}
            onClick={onNewBooking}
            startAdornment={<AddIcon fontSize="small" />}
          >
            {t("toolbar.new_booking")}
          </Button>
        )}

        {!isVendor && onBookAppointment && (
          <Button
            variant="contained"
            color="warning"
            onClick={onBookAppointment}
            startAdornment={<AddIcon fontSize="small" />}
          >
            {t("toolbar.book_appointment")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Toolbar;
